import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { UntilDestroy } from '@ngneat/until-destroy';
import { type Call, Device } from '@twilio/voice-sdk';
import { isNil } from 'lodash';
import { BehaviorSubject, distinctUntilChanged, firstValueFrom, from, map, Observable, Subject } from 'rxjs';

import {
  CallEntrypoint,
  CallList,
  CallLog,
  CommunicationSessionType,
  CommunicationsResponseConferenceParticipantCreate,
  SoftDeviceWebhookAction,
} from '@pwp-common';

import { createVoiceSdkDevice } from '../../../../common/voice-sdk/create-voice-sdk-device';
import { CallEntrypointsService } from '../../../../services/call/call-entrypoints/call-entrypoints.service';
import { CallListService } from '../../../../services/call/call-list/call-list.service';
import { CallLogService } from '../../../../services/call/call-log/call-log.service';
import { CommunicationsService } from '../../../../services/call/communications/communications.service';
import { joinCallEntrypointAndCallListNames } from '../helpers/join-call-entrypoint-and-call-list-names';
import type {
  AudioDeviceSelection,
  IncomingCallInput,
  OnCallDisconnectedInput,
  SessionInfo,
} from '../helpers/interfaces';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class SoftDeviceService {
  /////////////////////////////////////////////////////////////////////////////////////////////
  // Services
  /////////////////////////////////////////////////////////////////////////////////////////////
  private readonly communicationsService = inject(CommunicationsService);

  private readonly callLogService = inject(CallLogService);

  private readonly callListService = inject(CallListService);

  private readonly callEntrypointsService = inject(CallEntrypointsService);

  private readonly router = inject(Router);

  /////////////////////////////////////////////////////////////////////////////////////////////
  // State
  /////////////////////////////////////////////////////////////////////////////////////////////
  private receiverDevice?: Device;

  private callsInProgress$ = new BehaviorSubject<Map<string, Call>>(new Map());

  private callsOnHold$ = new BehaviorSubject<Set<string>>(new Set());

  private callOnMute$ = new BehaviorSubject<Set<string>>(new Set());

  public readonly incomingCall$ = new BehaviorSubject<IncomingCallInput | null>(null);

  public readonly incomingCallCanceled$ = new Subject<string>();

  public readonly onCallDisconnected$ = new Subject<OnCallDisconnectedInput>();

  private readonly ringingCallSids$ = new BehaviorSubject<Set<string>>(new Set());

  public readonly hasIncomingCalls$ = this.ringingCallSids$.pipe(
    map((sids) => sids.size > 0),
    distinctUntilChanged(),
  );

  /**
   * Device for each connected call, not the idle listener.
   */
  private readonly callDevices = new Map<string, Device>();

  private readonly audioDevicesState$ = new BehaviorSubject<AudioDeviceSelection>({
    inputs: [],
    outputs: [],
    isOutputSelectionSupported: false,
  });

  public readonly audioDevices$ = this.audioDevicesState$.asObservable();

  public readonly selectedInputDeviceId$ = new BehaviorSubject<string | null>(null);

  public readonly selectedOutputDeviceId$ = new BehaviorSubject<string | null>(null);

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Cache
  /////////////////////////////////////////////////////////////////////////////////////////////
  private callLogCache: Map<string, CallLog> = new Map();

  private sessionInfoCache: Map<string, SessionInfo> = new Map();

  private callListCache: Map<string, CallList> = new Map();

  private callEntrypointCache: Map<string, CallEntrypoint> = new Map();

  /**
   * Get CallLog by sessionId
   */
  private async getCallLog(sessionId: string) {
    if (this.callLogCache.has(sessionId)) {
      return this.callLogCache.get(sessionId);
    }

    const callLog = await firstValueFrom(this.callLogService.getDoc(sessionId));
    this.callLogCache.set(sessionId, callLog);

    return callLog;
  }

  /**
   * Get session info (sessionId and sessionType) by callSid
   */
  public async getSessionInfo(callSid: string): Promise<SessionInfo> {
    const cached = this.sessionInfoCache.get(callSid);
    if (cached) {
      return cached;
    }

    const response = await this.communicationsService.getSessionIdFromCallSid(callSid);
    const sessionId = response.getSessionId();
    if (response.getError() || isNil(sessionId)) {
      throw new Error('Error. This call SID is not found');
    }

    const sessionInfo: SessionInfo = {
      sessionId,
      sessionType: response.getSessionType(),
    };
    this.sessionInfoCache.set(callSid, sessionInfo);

    return sessionInfo;
  }

  /**
   * Get CallList by callListId
   */
  private async getCallList(callListId: string | undefined) {
    if (!callListId) {
      return undefined;
    }
    if (this.callListCache.has(callListId)) {
      return this.callListCache.get(callListId);
    }

    const callList = await firstValueFrom(this.callListService.getDoc(callListId));
    if (callList !== undefined) {
      this.callListCache.set(callListId, callList);
    }

    return callList;
  }

  /**
   * Get CallEntrypoint by callEntrypointId
   */
  private async getCallEntrypoint(callEntrypointId: string | undefined) {
    if (!callEntrypointId) {
      return undefined;
    }
    if (this.callEntrypointCache.has(callEntrypointId)) {
      return this.callEntrypointCache.get(callEntrypointId);
    }

    const callEntrypoint = await firstValueFrom(this.callEntrypointsService.getDoc(callEntrypointId));
    if (callEntrypoint !== undefined) {
      this.callEntrypointCache.set(callEntrypointId, callEntrypoint);
    }

    return callEntrypoint;
  }

  /**
   * Loads communication type detail and updates the existing call toast notification
   */
  private async loadCommunicationTypeDetail(callSid: string): Promise<void> {
    const { sessionId, sessionType } = await this.getSessionInfo(callSid);

    if (sessionType === CommunicationSessionType.inboundConversation) {
      const incomingChatData = this.incomingCall$.value;
      if (incomingChatData?.callSid === callSid) {
        this.incomingCall$.next({
          ...incomingChatData,
          isConversation: true,
          shouldDisplayNewToast: false,
        });
      }
      return;
    }

    const callLog = await this.getCallLog(sessionId);
    const [callList, callEntrypoint] = await Promise.all([
      this.getCallList(callLog?.getCallListId()),
      this.getCallEntrypoint(callLog?.getCallEntrypointId()),
    ]);

    const incomingCallData = this.incomingCall$.value;
    if (incomingCallData?.callSid === callSid) {
      this.incomingCall$.next({
        ...incomingCallData,
        isConversation: false,
        communicationTypeDetail: joinCallEntrypointAndCallListNames(
          callEntrypoint?.getDisplayName(),
          callList?.getDisplayName(),
        ),
        shouldDisplayNewToast: false,
      });
    }
  }

  public readonly hasInProgressCalls$ = this.callsInProgress$.pipe(
    map((inProgressCalls) => inProgressCalls.size > 0),
    distinctUntilChanged(),
  );

  public readonly inProgressCallSessionIds$ = this.callsInProgress$.pipe(
    map((inProgressCalls) => new Set(inProgressCalls.keys())),
  );

  public readonly latestInProgressCallSessionId$ = this.callsInProgress$.pipe(
    map((inProgressCalls) => {
      const keys = Array.from(inProgressCalls.keys());
      return keys.length > 0 ? keys[keys.length - 1] : null;
    }),
    distinctUntilChanged(),
  );

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Twilio Integration
  /////////////////////////////////////////////////////////////////////////////////////////////

  /**
   * Initializes the Twilio device and sets up event listeners for incoming calls
   */
  public async startTwilioListener(): Promise<void> {
    if (this.receiverDevice) {
      return;
    }

    try {
      console.log('[SoftDevice] starting Twilio listener');
      this.receiverDevice = await this.createVoiceDevice(true);
      this.receiverDevice.audio?.on('deviceChange', this.publishAudioDevices);
      this.publishAudioDevices();

      this.receiverDevice.on(Device.EventName.Incoming, async (call: Call) => {
        const callSid = call.parameters.CallSid;

        call.on('cancel', () => {
          this.removeRingingCall(callSid);
          this.incomingCallCanceled$.next(callSid);
          if (this.incomingCall$.value?.callSid === callSid) {
            this.incomingCall$.next(null);
          }
        });

        this.addRingingCall(callSid);
        this.incomingCall$.next({
          call,
          callSid,
          connectToken: call.connectToken,
          callerNumber: call.parameters.From,
          shouldDisplayNewToast: true,
        });

        try {
          await this.loadCommunicationTypeDetail(callSid);
        } catch (error) {
          console.warn('[SoftDevice] Could not load communication type detail', error);
          const incomingCallData = this.incomingCall$.value;
          if (incomingCallData?.callSid === callSid) {
            this.incomingCall$.next({
              ...incomingCallData,
              isConversation: false,
              shouldDisplayNewToast: false,
            });
          }
        }
      });

      await this.receiverDevice.register();
    } catch (error) {
      this.destroyReceiverDevice();
      throw error;
    }
  }

  /**
   * Ensures the Twilio receiver is started, optionally forcing a restart
   */
  public async ensureTwilioListener(forceRestart = false): Promise<void> {
    if (forceRestart) {
      this.destroyReceiverDevice();
    }

    await this.startTwilioListener();
  }

  /**
   * Stops the Twilio listener and cleans up resources
   */
  public stopTwilioListener(): void {
    this.destroyReceiverDevice();
    this.resetAllCallStates();
  }

  /**
   * Destroys the Twilio receiver device and clears ringing state
   */
  private destroyReceiverDevice(): void {
    if (this.receiverDevice) {
      console.log('[SoftDevice] destroying Twilio listener');
      this.receiverDevice.audio?.removeListener('deviceChange', this.publishAudioDevices);
      this.receiverDevice.destroy();
      this.receiverDevice = undefined;
    }
    this.ringingCallSids$.next(new Set());
    this.incomingCall$.next(null);
    this.publishAudioDevices();
  }

  /**
   * Handles accepting an incoming call
   */
  public async onAcceptCall(data: IncomingCallInput) {
    this.removeRingingCall(data.call.parameters.CallSid);
    const { sessionId, sessionType } = await this.getSessionInfo(data.call.parameters.CallSid);

    if (sessionType === CommunicationSessionType.inboundConversation) {
      return Promise.all([
        this.router.navigate(['/chat'], { queryParams: { conversationLogId: sessionId } }),
        this.acceptConversationOfferCall(data.connectToken),
      ]);
    }

    return Promise.all([
      this.router.navigate([`automation/contact-center/${sessionId}`]),
      this.forwardCall(data.connectToken, sessionId),
    ]);
  }

  /**
   * Handles rejecting an incoming call
   */
  public onRejectCall(data: IncomingCallInput): void {
    this.removeRingingCall(data.call.parameters.CallSid);
    data.call.reject();
  }

  private async acceptConversationOfferCall(connectToken: string): Promise<void> {
    const device = await this.createVoiceDevice();

    const call = await device.connect({ connectToken });
    call.on('disconnect', () => device.destroy());
  }

  /**
   * Forwards a call using the provided tokens
   */
  public async forwardCall(connectToken: string, sessionId: string) {
    // For each incoming call, create a new Device object. This ensures individual
    // interaction with each call, without affecting other calls.
    // IMPORTANT: The token for this new device needs to have the same identity
    // as the token used in the receiverDevice.
    const device = await this.createVoiceDevice();

    const call = await device.connect({ connectToken });
    this.setCallState(sessionId, call, device);
    call.on('disconnect', async () => {
      device.destroy();
      await this.handleOnCallDisconnected(sessionId);
      this.resetCallState(sessionId);
    });
  }

  /**
   * Starts listening to an ongoing call
   */
  public async startListeningToAnOngoingCall(orgId: string, sessionId: string): Promise<Call> {
    const device = await this.createVoiceDevice();

    const call = await device.connect({
      params: {
        orgId,
        sessionId,
        softDeviceAction: SoftDeviceWebhookAction.joinOngoingConference,
      },
    });
    this.setCallState(sessionId, call, device);
    call.on('accept', () => {
      call.mute(true);
      const callOnMute = new Set(this.callOnMute$.value);
      callOnMute.add(sessionId);
      this.callOnMute$.next(callOnMute);
    });

    call.on('disconnect', () => {
      device.destroy();
      this.resetCallState(sessionId);
    });

    return call;
  }

  /**
   * Sends digits to the active call
   */
  public sendDigit(digit: string, sessionId: string): void {
    this.callsInProgress$.value.get(sessionId)?.sendDigits(digit);
  }

  /**
   * Disconnects the active call
   */
  public disconnectCall(sessionId: string): void {
    this.callsInProgress$.value.get(sessionId)?.disconnect();
    this.resetCallState(sessionId);
  }

  /**
   * Toggles hold state for the current call
   */
  public async toggleHold(sessionId: string) {
    const callLog = await this.getCallLog(sessionId);
    const isOnHold = this.callsOnHold$.value.has(sessionId);

    await this.communicationsService.updateConferenceParticipant(sessionId, callLog?.getIncomingCallSid(), !isOnHold);

    const callsOnHold = new Set(this.callsOnHold$.value);
    if (isOnHold) {
      callsOnHold.delete(sessionId);
    } else {
      callsOnHold.add(sessionId);
    }
    this.callsOnHold$.next(callsOnHold);
  }

  /**
   * Toggles mute state for a conference participant using Twilio Voice SDK directly
   */
  public toggleMute(sessionId: string): void {
    const sessionCall = this.callsInProgress$.value.get(sessionId);

    if (!sessionCall) {
      throw new Error('Error: Call not found or not connected');
    }
    sessionCall.mute(!sessionCall.isMuted());

    const callsOnMute = new Set(this.callOnMute$.value);

    if (sessionCall.isMuted()) {
      callsOnMute.add(sessionId);
    } else {
      callsOnMute.delete(sessionId);
    }
    this.callOnMute$.next(callsOnMute);
  }

  /**
   * Adds a conference participant to a call
   */

  public addConferenceParticipant(
    to: string,
    sessionId: string,
  ): Observable<CommunicationsResponseConferenceParticipantCreate> {
    return from(
      this.communicationsService.createConferenceParticipant({
        conferenceFriendlyName: sessionId,
        to,
      }),
    );
  }

  /**
   * Returns the number of people currently in the conference. Falls back to 0
   * when the query fails so hangup can use the normal two-person flow.
   */
  public async getConferenceParticipantCount(sessionId: string): Promise<number> {
    try {
      const response = await this.communicationsService.getConferenceParticipants(sessionId);
      if (response.getError()) {
        return 0;
      }
      return response.getParticipantCount();
    } catch {
      return 0;
    }
  }

  /**
   * Ends the conference for every remaining participant.
   */
  public async endConference(sessionId: string): Promise<void> {
    await this.communicationsService.endConference(sessionId);
  }

  /**
   * Checks if a call is on hold
   */
  public isCallOnHold$(sessionId: string): Observable<boolean> {
    return this.callsOnHold$.pipe(map((set) => set.has(sessionId)));
  }

  /**
   * Checks if a call is on mute
   */
  public isCallOnMute$(sessionId: string): Observable<boolean> {
    return this.callOnMute$.pipe(map((set) => set.has(sessionId)));
  }

  /**
   * Checks if a call is active
   */
  public isCallActive$(sessionId: string): Observable<boolean> {
    return this.callsInProgress$.pipe(
      map((calls) => calls.has(sessionId)),
      distinctUntilChanged(),
    );
  }

  /**
   * Notifies subscribers that the call has ended.
   */
  private async handleOnCallDisconnected(sessionId: string): Promise<void> {
    const callLog = await firstValueFrom(this.callLogService.getDoc(sessionId));
    const [callList, callEntrypoint] = await Promise.all([
      this.getCallList(callLog?.getCallListId()),
      this.getCallEntrypoint(callLog?.getCallEntrypointId()),
    ]);

    this.onCallDisconnected$.next({
      talkDurationMS: callLog?.getTalkDurationMS()!,
      communicationTypeDetail: joinCallEntrypointAndCallListNames(
        callEntrypoint?.getDisplayName(),
        callList?.getDisplayName(),
      ),
    });
  }

  /**
   * Resets a call state
   */
  private resetCallState(sessionId: string): void {
    this.callDevices.delete(sessionId);
    const inProgressCalls = new Map(this.callsInProgress$.value);
    inProgressCalls.delete(sessionId);
    this.callsInProgress$.next(inProgressCalls);
    this.callsOnHold$.next(new Set([...this.callsOnHold$.value].filter((callId) => callId !== sessionId)));
    this.callOnMute$.next(new Set([...this.callOnMute$.value].filter((callId) => callId !== sessionId)));

    for (const [callSid, cachedSessionInfo] of Array.from(this.sessionInfoCache.entries())) {
      if (cachedSessionInfo.sessionId === sessionId) {
        this.sessionInfoCache.delete(callSid);
      }
    }

    this.callLogCache.delete(sessionId);
  }

  /**
   * Set a call state
   */
  private setCallState(sessionId: string, call: Call, device: Device): void {
    this.callDevices.set(sessionId, device);
    const inProgressCalls = new Map(this.callsInProgress$.value);
    inProgressCalls.set(sessionId, call);
    this.callsInProgress$.next(inProgressCalls);
  }

  /**
   * Resets all call related states
   */
  private resetAllCallStates(): void {
    for (const sessionId of this.callsInProgress$.value.keys()) {
      this.disconnectCall(sessionId);
    }
    this.callsInProgress$.next(new Map());
    this.callsOnHold$.next(new Set());
    this.callOnMute$.next(new Set());
    this.ringingCallSids$.next(new Set());
    this.callLogCache.clear();
    this.sessionInfoCache.clear();
    this.callListCache.clear();
    this.callEntrypointCache.clear();
    this.callDevices.clear();
    this.publishAudioDevices();
  }

  /**
   * Adds a ringing call SID to the tracked set
   */
  private addRingingCall(callSid: string): void {
    const ringingCallSids = new Set(this.ringingCallSids$.value);
    ringingCallSids.add(callSid);
    this.ringingCallSids$.next(ringingCallSids);
  }

  /**
   * Removes a ringing call SID from the tracked set
   */
  private removeRingingCall(callSid: string): void {
    const ringingCallSids = new Set(this.ringingCallSids$.value);
    if (!ringingCallSids.delete(callSid)) {
      return;
    }
    this.ringingCallSids$.next(ringingCallSids);
  }

  /**
   * Holds all in-progress calls
   */
  public async holdAllCalls(): Promise<void> {
    const callsToHold = Array.from(this.callsInProgress$.value.keys()).filter(
      (sessionId) => !this.callsOnHold$.value.has(sessionId),
    );

    await Promise.all(callsToHold.map((sessionId) => this.toggleHold(sessionId)));
  }

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Audio devices
  /////////////////////////////////////////////////////////////////////////////////////////////

  /**
   * Sets the microphone for in-progress and future calls.
   */
  public async setInputDevice(deviceId: string): Promise<void> {
    const previousDeviceId = this.selectedInputDeviceId$.value;
    this.selectedInputDeviceId$.next(deviceId);

    try {
      await this.applySelectedAudioDevicesToAll();
    } catch (error) {
      this.selectedInputDeviceId$.next(previousDeviceId);
      throw error;
    }
  }

  /**
   * Sets the speakers used for ringtone and call audio.
   */
  public async setOutputDevice(deviceId: string): Promise<void> {
    const previousDeviceId = this.selectedOutputDeviceId$.value;
    this.selectedOutputDeviceId$.next(deviceId);

    try {
      await this.applySelectedAudioDevicesToAll();
    } catch (error) {
      this.selectedOutputDeviceId$.next(previousDeviceId);
      throw error;
    }
  }

  /**
   * Creates a Twilio Device with the selected mic and speaker.
   * Pass true for the idle listener so it does not open the microphone.
   */
  private async createVoiceDevice(isIdleReceiver = false): Promise<Device> {
    const device = await createVoiceSdkDevice({
      getJWT: async () => {
        const result = await this.communicationsService.getVoiceDeviceToken();
        return result.getToken();
      },
    });

    try {
      await this.applySelectedAudioDevices(device, isIdleReceiver);
    } catch (error) {
      console.warn('[SoftDevice] Could not apply audio devices on a new Device', error);
    }

    return device;
  }

  /**
   * Applies the selected microphone and speaker to every live Twilio Device.
   */
  private async applySelectedAudioDevicesToAll(): Promise<void> {
    const devices = [this.receiverDevice, ...Array.from(this.callDevices.values())].filter(
      (device): device is Device => !isNil(device),
    );
    await Promise.all(devices.map((device) => this.applySelectedAudioDevices(device)));
  }

  /**
   * The idle listener does not open the mic, so Chrome does not show
   * "using microphone" while waiting for a call.
   */
  private async applySelectedAudioDevices(
    device: Device,
    isIdleReceiver = device === this.receiverDevice,
  ): Promise<void> {
    const { audio } = device;
    if (isNil(audio)) {
      return;
    }

    const selectedInputDeviceId = this.selectedInputDeviceId$.value;
    const selectedOutputDeviceId = this.selectedOutputDeviceId$.value;

    if (isIdleReceiver) {
      await audio.unsetInputDevice();
    } else if (selectedInputDeviceId) {
      await audio.setInputDevice(selectedInputDeviceId);
    }

    if (selectedOutputDeviceId && audio.isOutputSelectionSupported) {
      await Promise.all([
        audio.speakerDevices.set(selectedOutputDeviceId),
        audio.ringtoneDevices.set(selectedOutputDeviceId),
      ]);
    }
  }

  /**
   * Clears a selection if that device was unplugged.
   */
  private readonly publishAudioDevices = (): void => {
    const audio = this.receiverDevice?.audio ?? Array.from(this.callDevices.values())[0]?.audio;
    const selectedInputDeviceId = this.selectedInputDeviceId$.value;
    const selectedOutputDeviceId = this.selectedOutputDeviceId$.value;

    if (audio) {
      if (selectedInputDeviceId && !audio.availableInputDevices.has(selectedInputDeviceId)) {
        this.selectedInputDeviceId$.next(null);
      }

      if (selectedOutputDeviceId && !audio.availableOutputDevices.has(selectedOutputDeviceId)) {
        this.selectedOutputDeviceId$.next(null);
      }
    }

    this.audioDevicesState$.next({
      inputs: audio?.availableInputDevices ? Array.from(audio.availableInputDevices.values()) : [],
      outputs: audio?.availableOutputDevices ? Array.from(audio.availableOutputDevices.values()) : [],
      isOutputSelectionSupported: audio?.isOutputSelectionSupported ?? false,
    });
  };
}
